if (!customElements.get("klaviyo-club-form")) {
  class KlaviyoClubForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.form = this.querySelector("form");
      this.message = this.querySelector(".klaviyo-club-form__message");
      this.messageText = this.message?.querySelector("[data-club-form-message-text]") || this.message;
      this.button = this.form?.querySelector('[type="submit"]');

      if (!this.form || !this.message || !this.button) {
        return;
      }

      this.initialized = true;
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
    }

    diagnosticErrorName(error) {
      const name = error?.name;
      return typeof name === "string" && /^[A-Za-z][A-Za-z0-9]{0,39}$/.test(name)
        ? name
        : "Error";
    }

    redactDiagnosticText(value, limit) {
      return (typeof value === "string" ? value : "")
        .replace(/https?:\/\/[^\s"'<>]+/gi, "[url]")
        .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")
        .slice(0, limit);
    }

    async responseErrorCode(response) {
      if (typeof response.json !== "function") {
        return null;
      }

      try {
        const payload = await response.json();
        const code = payload?.errors?.[0]?.code;
        return typeof code === "string" && /^[A-Za-z][A-Za-z0-9._-]{0,63}$/.test(code)
          ? code
          : null;
      } catch {
        return null;
      }
    }

    async subscribe(email, listId) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${this.dataset.companyId}`,
          {
            method: "POST",
            signal: controller.signal,
            headers: {
              revision: "2024-02-15",
              "content-type": "application/json",
            },
            body: JSON.stringify({
              data: {
                type: "subscription",
                attributes: {
                  custom_source: this.dataset.source,
                  profile: {
                    data: {
                      type: "profile",
                      attributes: {
                        email,
                      },
                    },
                  },
                },
                relationships: {
                  list: {
                    data: {
                      type: "list",
                      id: listId,
                    },
                  },
                },
              },
            }),
          },
        );

        if (!response.ok) {
          const error = new Error(`Klaviyo subscription failed with status ${response.status}`);
          error.status = response.status;
          error.responseCode = await this.responseErrorCode(response);
          throw error;
        }

        return response.status;
      } catch (error) {
        const failure = new Error("Klaviyo subscription request failed");
        failure.status = error?.status ?? null;
        failure.errorType = failure.status !== null ? "http" : controller.signal.aborted ? "timeout" : "network";
        failure.errorName = this.diagnosticErrorName(error);
        failure.diagnosticMessage = this.redactDiagnosticText(error?.message, 200);
        failure.diagnosticStack = this.redactDiagnosticText(error?.stack, 1000);
        failure.responseCode = error?.responseCode ?? null;
        throw failure;
      } finally {
        clearTimeout(timeout);
      }
    }

    getSelectedListIds() {
      const selectedListIds = Array.from(
        this.form.querySelectorAll("[data-klaviyo-list-id]:checked"),
        (input) => input.dataset.klaviyoListId,
      );

      return [...new Set([this.dataset.defaultListId, ...selectedListIds].filter(Boolean))];
    }

    showMessage(message, type = "") {
      this.messageText.textContent = message || "";
      this.message.hidden = !message;
      this.message.classList.remove("error", "success", "partial");

      if (type) {
        this.message.classList.add(type);
      }
    }

    setLoading(isLoading) {
      this.button.classList.toggle("button--working", isLoading);
      this.button.disabled = isLoading;
      this.form.setAttribute("aria-busy", isLoading.toString());
    }

    resetState() {
      if (!this.form || !this.message || !this.button || this.submitting) {
        return;
      }

      this.form.reset();
      this.showMessage(this.dataset.defaultMessage);
      this.setLoading(false);
    }

    reportClarityFailures(email, detail) {
      try {
        if (typeof window.clarity !== "function") {
          return null;
        }

        if (
          window.JWCThirdParty?.requireConsent &&
          !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()
        ) {
          return null;
        }

        const identity = Promise.resolve(
          window.clarity("identify", String(email).trim().toLowerCase()),
        ).catch(() => null);

        window.clarity("set", "klaviyo_club_form", this.form.id);
        window.clarity("set", "klaviyo_club_source", this.dataset.formSource || "CLUB FORM");
        window.clarity("set", "klaviyo_club_outcome", detail.outcome);
        window.clarity("set", "klaviyo_club_primary_succeeded", String(detail.primarySucceeded));
        window.clarity("set", "klaviyo_club_failed_list", detail.failedListIds);
        window.clarity("set", "klaviyo_club_failure_code", detail.failures.map((failure) =>
          `${failure.list_id}_${failure.error_type}_${failure.status ?? "unknown"}`,
        ));
        if (detail.clarityFailures?.length) {
          window.clarity("set", "klaviyo_club_error_name", detail.clarityFailures.map((failure) =>
            `${failure.list_id}_${failure.error_name}`,
          ));
          const responseCodes = detail.clarityFailures
            .filter((failure) => failure.response_code)
            .map((failure) => `${failure.list_id}_${failure.response_code}`);
          if (responseCodes.length) {
            window.clarity("set", "klaviyo_club_response_code", responseCodes);
          }
        }
        window.clarity("event", `klaviyo_club_form_${detail.outcome}`);
        window.clarity("upgrade", "klaviyo_club_form_failure");

        return { identity };
      } catch {
        return null;
      }
    }

    reportClarityTelemetryFailure(errorType, status, errorName, responseCode) {
      try {
        if (
          typeof window.clarity !== "function" ||
          (window.JWCThirdParty?.requireConsent &&
            !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.())
        ) {
          return;
        }

        window.clarity("set", "klaviyo_club_telemetry_failure", `${errorType}_${status ?? "unknown"}`);
        window.clarity("set", "klaviyo_club_telemetry_error_name", errorName);
        if (responseCode) {
          window.clarity("set", "klaviyo_club_telemetry_response_code", responseCode);
        }
        window.clarity("event", "klaviyo_club_telemetry_error");
      } catch {
        return;
      }
    }

    async reportFailures(email, detail) {
      this.reportClarityFailures(email, detail);
      let timeout;
      let controller;

      try {
        if (!this.dataset.companyId) {
          return;
        }

        if (
          window.JWCThirdParty?.requireConsent &&
          !window.Shopify?.customerPrivacy?.marketingAllowed?.()
        ) {
          return;
        }

        controller = new AbortController();
        timeout = setTimeout(() => controller.abort(), 10000);
        const response = await fetch(
          `https://a.klaviyo.com/client/events/?company_id=${this.dataset.companyId}`,
          {
            method: "POST",
            signal: controller.signal,
            keepalive: true,
            headers: {
              revision: "2024-02-15",
              "content-type": "application/json",
            },
            body: JSON.stringify({
              data: {
                type: "event",
                attributes: {
                  unique_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  metric: {
                    data: {
                      type: "metric",
                      attributes: { name: "Klaviyo Club Form Submission Failed" },
                    },
                  },
                  profile: { data: { type: "profile", attributes: { email } } },
                  properties: {
                    outcome: detail.outcome,
                    primary_succeeded: detail.primarySucceeded,
                    form_id: this.form.id,
                    form_source: this.dataset.formSource,
                    page_url: `${window.location.origin}${window.location.pathname}`,
                    selected_list_ids: detail.selectedListIds,
                    successful_list_ids: detail.successfulListIds,
                    failed_list_ids: detail.failedListIds,
                    failures: detail.failures,
                  },
                },
              },
            }),
          },
        );

        if (!response.ok) {
          const error = new Error(`Klaviyo failure event failed with status ${response.status}`);
          error.status = response.status;
          error.responseCode = await this.responseErrorCode(response);
          throw error;
        }
      } catch (error) {
        const status = error?.status ?? null;
        const errorType = status !== null ? "http" : controller?.signal.aborted ? "timeout" : "network";
        const errorName = this.diagnosticErrorName(error);
        console.warn("Unable to report Klaviyo club form failure", {
          status,
          errorType,
          errorName,
          responseCode: error?.responseCode ?? null,
          message: this.redactDiagnosticText(error?.message, 200),
          stack: this.redactDiagnosticText(error?.stack, 1000),
        });
        this.reportClarityTelemetryFailure(errorType, status, errorName, error?.responseCode ?? null);
      } finally {
        clearTimeout(timeout);
      }
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.submitting) {
        return;
      }

      this.submitting = true;
      const downloadLink = this.querySelector('[data-club-download-link]');
      if (downloadLink) {
        downloadLink.hidden = true;
        downloadLink.querySelector('a').removeAttribute('href');
      }
      const data = new FormData(this.form);
      const email = data.get("email");
      const listIds = this.getSelectedListIds();

      this.showMessage(this.dataset.defaultMessage);
      this.setLoading(true);

      try {
        if (!this.dataset.defaultListId || !this.dataset.companyId) {
          throw new Error("Klaviyo club form configuration is incomplete");
        }

        const results = await Promise.allSettled(
          listIds.map((listId) => this.subscribe(email, listId)),
        );
        const successfulListIds = listIds.filter((_, index) => results[index].status === "fulfilled");
        const clarityFailures = [];
        const consoleDiagnostics = [];
        const failures = results.flatMap((result, index) => {
          if (result.status === "fulfilled") {
            return [];
          }

          const reason = result.reason;
          clarityFailures.push({
            list_id: listIds[index],
            error_name: reason.errorName,
            response_code: reason.responseCode,
          });
          consoleDiagnostics.push({
            list_id: listIds[index],
            error_name: reason.errorName,
            response_code: reason.responseCode,
            message: reason.diagnosticMessage,
            stack: reason.diagnosticStack,
          });
          return [{
            list_id: listIds[index],
            status: reason.status ?? null,
            error_type: reason.errorType,
          }];
        });
        const failedListIds = failures.map((failure) => failure.list_id);
        const primarySucceeded = successfulListIds.includes(this.dataset.defaultListId);
        const outcome = failures.length === 0 ? "success" : successfulListIds.length ? "partial" : "error";
        const detail = {
          email,
          listIds: successfulListIds,
          selectedListIds: listIds,
          successfulListIds,
          failedListIds,
          failures,
          clarityFailures,
          primarySucceeded,
          outcome,
        };

        if (outcome === "success") {
          this.form.reset();
          this.showMessage(this.dataset.successMessage, "success");
        } else {
          console.error("Klaviyo club form subscription failed", {
            formId: this.form.id, failures, diagnostics: consoleDiagnostics,
          });
          void this.reportFailures(email, detail);

          if (outcome === "partial") {
            for (const input of this.form.querySelectorAll("[data-klaviyo-list-id]:checked")) {
              if (successfulListIds.includes(input.dataset.klaviyoListId)) {
                input.checked = false;
              }
            }

            this.showMessage(
              primarySucceeded ? this.dataset.partialMessage : this.dataset.primaryErrorMessage,
              "partial",
            );
          } else {
            detail.error = new Error("All Klaviyo club subscriptions failed");
            this.showMessage(this.dataset.errorMessage, "error");
          }
        }

        this.dispatchEvent(
          new CustomEvent(`klaviyo:club-form:${outcome}`, {
            bubbles: true,
            detail,
          }),
        );
      } catch (error) {
        console.error(error);
        this.showMessage(this.dataset.errorMessage, "error");
        const detail = {
          email,
          error,
          listIds: [],
          selectedListIds: listIds,
          successfulListIds: [],
          failedListIds: listIds,
          failures: [{ list_id: this.dataset.defaultListId, status: null, error_type: "unexpected" }],
          clarityFailures: [{
            list_id: this.dataset.defaultListId,
            error_name: this.diagnosticErrorName(error),
            response_code: null,
          }],
          primarySucceeded: false,
          outcome: "error",
        };
        void this.reportFailures(email, detail);
        this.dispatchEvent(
          new CustomEvent("klaviyo:club-form:error", {
            bubbles: true,
            detail,
          }),
        );
      } finally {
        this.submitting = false;
        this.setLoading(false);
      }
    }
  }

  customElements.define("klaviyo-club-form", KlaviyoClubForm);
}
